import { z } from 'zod';
import {
  supportTicketCategories,
  supportTicketPriorities,
  supportTicketStatuses,
  type SupportMessageAuthorKind,
  type SupportTicketCategory,
  type SupportTicketPriority,
  type SupportTicketStatus,
} from '../enums.js';
import { paginationFields, type PaginatedDto } from './_pagination.js';

/**
 * Assistance : un cabinet écrit, la plateforme répond.
 *
 * **Un ticket n'est pas un dossier.** Rien dans ce contrat ne demande ni ne rend de donnée de
 * patient, et le produit n'en met aucune : un cabinet reste libre d'en écrire dans son texte —
 * on ne peut pas l'en empêcher —, d'où la conséquence tenue côté serveur, que les notifications
 * par email ne recopient **jamais** le contenu et renvoient vers l'application.
 *
 * Les mêmes formes servent des deux côtés. Ce qui change est la route empruntée, donc la garde et
 * le côté d'où vient le message : le serveur pose `authorKind` d'après la route, jamais d'après
 * le corps, sans quoi un cabinet pourrait écrire un message qui a l'air de venir de nous.
 */

/* -------------------------------------------------------------------------- */
/* Entrées                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Plafond du corps d'un message. Généreux — un praticien décrit un problème —, et borné quand
 * même : une zone de texte sans limite est une façon ordinaire de remplir une base.
 */
const messageBody = z.string().trim().min(1).max(5000);

/**
 * 8 Mo par fichier. Le dossier patient accepte 25 Mo parce qu'une imagerie ne passait pas ;
 * un ticket n'est pas un dossier, et la limite clinique ne s'y recopie pas.
 */
export const SUPPORT_ATTACHMENT_MAX_BYTES = 8 * 1024 * 1024;

/** Quelques fichiers par message : assez pour une capture et un PDF, pas un dépôt. */
export const SUPPORT_ATTACHMENT_MAX_COUNT = 4;

/**
 * Images matricielles et PDF. WebP et GIF sont déjà des types d'image sûrs du produit.
 * Pas de SVG (XML, donc script), pas de HTML, pas d'exécutable, pas de document bureautique.
 */
export const supportAttachmentMimeTypes = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'application/pdf',
] as const;
export type SupportAttachmentMimeType = (typeof supportAttachmentMimeTypes)[number];

const SUPPORT_ATTACHMENT_IMAGES = new Set<string>([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
]);

export function isSupportAttachmentMime(value: string): value is SupportAttachmentMimeType {
  return (supportAttachmentMimeTypes as readonly string[]).includes(value);
}

/** Une image se voit dans le fil. Un PDF, et tout le reste, se télécharge. */
export function supportAttachmentIsImage(mimeType: string): boolean {
  return SUPPORT_ATTACHMENT_IMAGES.has(mimeType);
}

export const supportTicketCreateSchema = z
  .object({
    subject: z.string().trim().min(3).max(200),
    category: z.enum(supportTicketCategories),
    priority: z.enum(supportTicketPriorities).default('normale'),
    /** Premier message du fil. Un ticket sans texte n'apprendrait rien à personne. */
    message: messageBody,
  })
  .strict();
export type SupportTicketCreateInput = z.infer<typeof supportTicketCreateSchema>;

export const supportMessageCreateSchema = z.object({ body: messageBody }).strict();
export type SupportMessageCreateInput = z.infer<typeof supportMessageCreateSchema>;

/**
 * Les mêmes champs que la création JSON, **sans `.strict()`** : le corps multipart porte aussi
 * le champ binaire `files`, lu à part. Une clé inconnue est ignorée, jamais écrite.
 */
export const supportTicketCreateFormSchema = z.object({
  subject: z.string().trim().min(3).max(200),
  category: z.enum(supportTicketCategories),
  priority: z.enum(supportTicketPriorities).default('normale'),
  message: messageBody,
});

export const supportMessageCreateFormSchema = z.object({ body: messageBody });

export const supportTicketIdParamSchema = z.object({ id: z.uuid() }).strict();

export const supportAttachmentParamSchema = z
  .object({ id: z.uuid(), attachmentId: z.uuid() })
  .strict();

export const supportTicketListQuerySchema = z
  .object({
    status: z.enum(supportTicketStatuses).optional(),
    ...paginationFields,
  })
  .strict();
export type SupportTicketListQuery = z.infer<typeof supportTicketListQuerySchema>;

/** File de la plateforme : la même, plus le cabinet et la catégorie comme filtres. */
export const platformTicketListQuerySchema = z
  .object({
    status: z.enum(supportTicketStatuses).optional(),
    category: z.enum(supportTicketCategories).optional(),
    organizationId: z.uuid().optional(),
    ...paginationFields,
  })
  .strict();
export type PlatformTicketListQuery = z.infer<typeof platformTicketListQuerySchema>;

/* -------------------------------------------------------------------------- */
/* Sorties                                                                     */
/* -------------------------------------------------------------------------- */

export interface SupportTicketDto {
  id: string;
  subject: string;
  category: SupportTicketCategory;
  priority: SupportTicketPriority;
  /**
   * Support prioritaire : vrai si l'offre du cabinet l'incluait **au moment de l'ouverture**.
   * Distinct de `priority`, que le cabinet demande : celui-ci, c'est nous qui l'accordons, d'après
   * l'offre, et il ne figure dans aucun schéma d'entrée.
   */
  prioritySupport: boolean;
  status: SupportTicketStatus;
  /** Horodatage du dernier message, quel qu'en soit l'auteur. C'est sur lui que la file se trie. */
  lastMessageAt: string;
  closedAt: string | null;
  createdAt: string;
  /** Nom de l'auteur, pour que le cabinet reconnaisse son propre fil. Jamais son adresse. */
  createdByName: string | null;
  /**
   * Cabinet du ticket. **Nul du côté cabinet**, où il est toujours le sien et où le répéter
   * n'apprendrait rien ; renseigné du côté plateforme, qui voit plusieurs cabinets.
   */
  organizationId: string | null;
  organizationName: string | null;
  messageCount: number;
}

/**
 * Pièce d'un message. Pas de clé de stockage, pas de chemin : le contenu se lit par une route
 * qui nomme le ticket et la pièce, sous la session.
 */
export interface SupportAttachmentDto {
  id: string;
  /** Nom d'affichage nettoyé. Il ne participe à aucun chemin. */
  fileName: string;
  /** Type détecté sur les octets, pas celui annoncé par le navigateur. */
  mimeType: string;
  sizeBytes: number;
  /** Vrai pour une image affichée dans le fil. Un PDF est toujours faux : il se télécharge. */
  inline: boolean;
}

export interface SupportMessageDto {
  id: string;
  authorKind: SupportMessageAuthorKind;
  /** Nom de l'auteur. Nul si son compte a été effacé : le fil reste lisible sans lui. */
  authorName: string | null;
  body: string;
  createdAt: string;
  attachments: SupportAttachmentDto[];
}

/** Un fil complet : son en-tête et ses messages, dans l'ordre. */
export interface SupportTicketThreadDto {
  ticket: SupportTicketDto;
  messages: SupportMessageDto[];
}

export type SupportTicketListDto = PaginatedDto<SupportTicketDto>;
